/**
 * Helpers for the "First sends" schedule badge rendered under each
 * recipient row (spec §6.2.5, Figma `28:68398`).
 *
 * The backend computes a `first_step_due_at` ISO timestamp per candidate
 * inside the preview session. The badge collapses that timestamp into one
 * of three short labels:
 *
 *   - "Today"     — `due_at` lands in the same calendar day as `now`
 *   - "Tomorrow"  — exactly one calendar day after `now`
 *   - <weekday>   — anything further out, surfaced as the short weekday
 *                   ("Mon" / "Tue" / "Wed" / ...)
 *
 * All comparisons happen in the **sender's timezone** (not the viewer's)
 * so a recruiter in Chicago previewing a sender in Tokyo sees the same
 * "Today" the sender's mailbox-cap heuristic computed against.
 *
 * The fallback used when the backend hasn't computed a schedule yet (or
 * the candidate is in the Fix-Issues bucket so a schedule is impossible)
 * is the literal `"—"` so the row reserves the same vertical space as a
 * filled badge.
 */

const SHORT_WEEKDAYS = [
  "Sun",
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
] as const;

/**
 * Return the short label for a candidate's first scheduled send.
 *
 * @param dueAtIso  Backend-computed first step `due_at`, or `null` when
 *                  the candidate is blocked / unscheduled.
 * @param senderTz  Timezone of the sender (e.g. `"America/Los_Angeles"`).
 *                  Falls back to the viewer's tz on `Invalid` input.
 * @param nowIso    Reference "now" timestamp; defaults to the current
 *                  wall clock. Threading this through enables tests to
 *                  freeze time without faking the system clock.
 */
export function firstSendBadge(
  dueAtIso: string | null | undefined,
  senderTz: string | null | undefined,
  nowIso: string = new Date().toISOString(),
): string {
  if (!dueAtIso) return "—";

  const due = new Date(dueAtIso);
  const now = new Date(nowIso);
  if (Number.isNaN(due.getTime()) || Number.isNaN(now.getTime())) return "—";

  // Translate both moments into the sender tz to compute "calendar day"
  // boundaries — a UTC-only comparison would drift by up to a day for
  // senders far from UTC.
  const tz = senderTz || undefined;
  const dueParts = partsInTz(due, tz);
  const nowParts = partsInTz(now, tz);

  const dueDayKey = `${dueParts.year}-${dueParts.month}-${dueParts.day}`;
  const nowDayKey = `${nowParts.year}-${nowParts.month}-${nowParts.day}`;

  if (dueDayKey === nowDayKey) return "Today";

  const tomorrow = new Date(now.getTime());
  tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
  const tomorrowParts = partsInTz(tomorrow, tz);
  const tomorrowKey = `${tomorrowParts.year}-${tomorrowParts.month}-${tomorrowParts.day}`;
  if (dueDayKey === tomorrowKey) return "Tomorrow";

  // Fall back to the short weekday name (computed in the sender tz so a
  // Friday-night-Tokyo cohort doesn't render as "Sat" for a US viewer).
  const weekdayIdx = weekdayIndexInTz(due, tz);
  return SHORT_WEEKDAYS[weekdayIdx] ?? "—";
}

function formatTime(due: Date, tz: string | undefined): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(due);
}

/** Abbreviated timezone (e.g. "ET", "PT") rather than the long
 * "Eastern Time" form, per design. Falls back to the short form
 * ("EDT") when the generic short isn't available. */
function tzAbbreviation(due: Date, tz: string | undefined): string | null {
  for (const style of ["shortGeneric", "short"] as const) {
    try {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: tz,
        timeZoneName: style,
      }).formatToParts(due);
      const name = parts.find((p) => p.type === "timeZoneName")?.value;
      if (name) return name;
    } catch {
      // try next style
    }
  }
  return null;
}

/**
 * First-send label for the recipient card badge — "Today at 1:21 PM" /
 * "Tomorrow at 9:00 AM" / "Mon at 9:00 AM". Combines the relative day
 * (from `firstSendBadge`) with the time, in the sender's timezone.
 * Returns `null` when there's no scheduled time.
 */
export function firstSendLabel(
  dueAtIso: string | null | undefined,
  senderTz: string | null | undefined,
  nowIso: string = new Date().toISOString(),
): string | null {
  if (!dueAtIso) return null;
  const due = new Date(dueAtIso);
  if (Number.isNaN(due.getTime())) return null;
  const day = firstSendBadge(dueAtIso, senderTz, nowIso);
  if (day === "—") return null;
  return `${day} at ${formatTime(due, senderTz || undefined)}`;
}

/**
 * Compact first-send label for the recipient card — "Executes on Aug 4
 * at 1:21 PM". Shows the concrete date + time (no relative "Today"),
 * formatted in the sender's timezone. Returns `null` when unscheduled.
 */
export function firstSendDateLabel(
  dueAtIso: string | null | undefined,
  senderTz: string | null | undefined,
): string | null {
  if (!dueAtIso) return null;
  const due = new Date(dueAtIso);
  if (Number.isNaN(due.getTime())) return null;
  const tz = senderTz || undefined;
  try {
    const date = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      month: "short",
      day: "numeric",
    }).format(due);
    return `Executes around ${date} at ${formatTime(due, tz)}`;
  } catch {
    return null;
  }
}

/**
 * Absolute first-send ETA line for the first step card (Figma
 * `10356:10932`). Phrased "on or around" because the exact send time is
 * subject to the sequence's send window + 2-minute allocator. Formatted
 * in the SENDER's timezone with an abbreviated tz ("ET"). Returns `null`
 * when there is no due time yet.
 */
export function formatExecuteEta(
  dueAtIso: string | null | undefined,
  senderTz: string | null | undefined,
): string | null {
  if (!dueAtIso) return null;
  const due = new Date(dueAtIso);
  if (Number.isNaN(due.getTime())) return null;
  const tz = senderTz || undefined;
  try {
    const date = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      month: "long",
      day: "numeric",
      year: "numeric",
    }).format(due);
    const time = formatTime(due, tz);
    const tzName = tzAbbreviation(due, tz);
    return `This will execute on or around ${date} at ${time}${
      tzName ? ` ${tzName}` : ""
    }`;
  } catch {
    return null;
  }
}

interface DateParts {
  year: string;
  month: string;
  day: string;
}

function partsInTz(date: Date, tz: string | undefined): DateParts {
  try {
    const fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    const parts = fmt.formatToParts(date);
    const get = (type: string) =>
      parts.find((p) => p.type === type)?.value ?? "00";
    return { year: get("year"), month: get("month"), day: get("day") };
  } catch {
    // Invalid tz string falls through to UTC parts so the badge still
    // renders something sensible instead of throwing.
    return {
      year: String(date.getUTCFullYear()),
      month: String(date.getUTCMonth() + 1).padStart(2, "0"),
      day: String(date.getUTCDate()).padStart(2, "0"),
    };
  }
}

function weekdayIndexInTz(date: Date, tz: string | undefined): number {
  try {
    const fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      weekday: "short",
    });
    const label = fmt.format(date);
    const idx = SHORT_WEEKDAYS.findIndex((w) => w === label);
    return idx === -1 ? 0 : idx;
  } catch {
    return date.getUTCDay();
  }
}
